import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Save, Lock, Building2, Users, MapPin, X } from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader, LoadingPage, ErrorPage } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Input, Select, Checkbox } from '../../components/ui/Form.jsx';
import AddressInput from '../../components/AddressInput.jsx';

/**
 * Schema is conditional on create vs edit:
 *   - create: call_sign required (except chauffeurs under an operator)
 *   - edit: call_sign not sent (field disabled in UI, backend won't accept anyway)
 *
 * Round 7b.5: operator fields ("fleet partner" in the DB) with cross-field validation:
 *   - is_fleet_partner and fleet_partner_id are mutually exclusive
 *   - (Oct 2026) operator company name / licence / address are optional
 *
 * Round 7b.6: home address required (compliance). Operator address additionally
 * required when is_fleet_partner=true.
 *
 * Oct 2026: chauffeurs working UNDER an operator only need a first name.
 * Email, phone, home address and call sign become optional for them. Job
 * emails for them go to the operator; "also_email_chauffeur" sends a copy.
 */
const baseSchema = {
    first_name: z.string().trim().min(1, 'First name is required').max(100),
    last_name: z.string().trim().max(100).optional().or(z.literal('')),
    email: z.string().trim().email('Enter a valid email').max(255).optional().or(z.literal('')),
    phone: z.string().trim().max(30).optional().or(z.literal('')),
    dob: z.string().optional().or(z.literal('')),
    ni_number: z.string().trim().max(20).optional().or(z.literal('')),
    driving_licence_number: z.string().trim().max(30).optional().or(z.literal('')),
    phv_licence_number: z.string().trim().max(30).optional().or(z.literal('')),
    start_date: z.string().min(1, 'Start date is required'),
    end_date: z.string().optional().or(z.literal('')),
    licensing_authority: z.string().trim().min(1, 'Licensing authority is required').max(100),
    include_in_tfl_report: z.boolean(),
    emergency_contact_name: z.string().trim().max(150).optional().or(z.literal('')),
    emergency_contact_relationship: z.string().trim().max(50).optional().or(z.literal('')),
    emergency_contact_phone: z.string().trim().max(30).optional().or(z.literal('')),
    is_fleet_partner: z.boolean().default(false),
    fleet_partner_id: z.string().nullable().optional().or(z.literal('')),
    fleet_partner_company_name: z.string().trim().max(200).nullable().optional().or(z.literal('')),
    fleet_partner_operator_licence_number: z.string().trim().max(50).nullable().optional().or(z.literal('')),
    fleet_partner_operator_licence_expires_at: z.string().nullable().optional().or(z.literal('')),
    // Round 7b.6 — addresses (single canonical string in *_line1)
    home_address_line1: z.string().trim().max(255).optional().or(z.literal('')),
    home_postcode: z.string().trim().max(20).nullable().optional().or(z.literal('')),
    operator_address_line1: z.string().trim().max(255).nullable().optional().or(z.literal('')),
    operator_postcode: z.string().trim().max(20).nullable().optional().or(z.literal('')),
    // Oct 2026 — chauffeurs under an operator: also email the chauffeur
    also_email_chauffeur: z.boolean().optional(),
    // Oct 2026 — cities / countries covered (job offers + chauffeur list filter)
    service_areas: z.array(z.string()).optional(),
};

const fleetRefine = (data, ctx) => {
    if (data.is_fleet_partner && data.fleet_partner_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['fleet_partner_id'],
            message: 'An operator cannot also work under another operator',
        });
    }
    // Oct 2026 — operators: first + last name only (outside Lanz CTS compliance,
    // often overseas). Chauffeurs under an operator: first name only.
    // Direct chauffeurs: contact details, home address and (on create) a call sign.
    const underOperator = !data.is_fleet_partner && Boolean(data.fleet_partner_id);
    if (data.is_fleet_partner) {
        if (!data.last_name) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['last_name'], message: 'Last name is required' });
        }
    } else if (!underOperator) {
        if (!data.last_name) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['last_name'], message: 'Last name is required' });
        }
        if (!data.email) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['email'], message: 'Email is required' });
        }
        if (!data.phone || data.phone.trim().length < 7) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phone'], message: 'Enter a valid phone number' });
        }
        if (!data.home_address_line1) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['home_address_line1'], message: 'Home address is required' });
        }
        // call_sign only exists in the create schema
        if (data.call_sign !== undefined && !data.call_sign) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['call_sign'], message: 'Call sign is required' });
        }
    } else if (data.also_email_chauffeur && !data.email) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['email'],
            message: 'Add an email to send the chauffeur a copy, or untick the box below',
        });
    }
};

const createSchema = z.object({
    ...baseSchema,
    call_sign: z.string()
        .trim()
        .max(30, 'Call sign must be 30 characters or fewer')
        .optional()
        .or(z.literal('')),
}).superRefine(fleetRefine);

const editSchema = z.object(baseSchema).superRefine(fleetRefine);

export function ChauffeurFormPage() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    // Opened from an operator's page ("Add chauffeur") → preselect that operator
    // and go back to the operator's page afterwards.
    const [searchParams] = useSearchParams();
    const presetOperatorId = !isEdit ? (searchParams.get('operator') || '') : '';
    const api = useApi();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { isAdmin } = useCurrentUser();

    const { data: existing, isLoading: isLoadingExisting, isError, error } = useQuery({
        queryKey: ['driver', id],
        queryFn: () => api.get(`/drivers/${id}`),
        enabled: isEdit,
    });

    // Operators list for the "Works under" dropdown
    const { data: fleetPartnersData } = useQuery({
        queryKey: ['fleet-partners'],
        queryFn: () => api.get('/drivers/meta/fleet-partners'),
    });
    const fleetPartners = (fleetPartnersData?.fleet_partners || [])
        .filter(p => !isEdit || p.id !== id); // never list self in dropdown

    const {
        register,
        handleSubmit,
        reset,
        setError,
        watch,
        control,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({
        resolver: zodResolver(isEdit ? editSchema : createSchema),
        defaultValues: {
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            dob: '',
            ni_number: '',
            driving_licence_number: '',
            phv_licence_number: '',
            call_sign: '',
            start_date: new Date().toISOString().slice(0, 10),
            end_date: '',
            licensing_authority: 'TFL',
            include_in_tfl_report: true,
            emergency_contact_name: '',
            emergency_contact_relationship: '',
            emergency_contact_phone: '',
            is_fleet_partner: false,
            fleet_partner_id: presetOperatorId,
            fleet_partner_company_name: '',
            fleet_partner_operator_licence_number: '',
            fleet_partner_operator_licence_expires_at: '',
            home_address_line1: '',
            home_postcode: '',
            operator_address_line1: '',
            operator_postcode: '',
            also_email_chauffeur: false,
            service_areas: ['London'],
        },
    });

    useEffect(() => {
        if (existing) {
            reset({
                first_name: existing.first_name || '',
                last_name: existing.last_name || '',
                email: existing.email || '',
                phone: existing.phone || '',
                dob: existing.dob || '',
                ni_number: existing.ni_number || '',
                driving_licence_number: existing.driving_licence_number || '',
                phv_licence_number: existing.phv_licence_number || '',
                call_sign: existing.call_sign || '',
                start_date: existing.start_date?.slice(0, 10) || '',
                end_date: existing.end_date?.slice(0, 10) || '',
                licensing_authority: existing.licensing_authority || 'TFL',
                include_in_tfl_report: existing.include_in_tfl_report ?? true,
                emergency_contact_name: existing.emergency_contact_name || '',
                emergency_contact_relationship: existing.emergency_contact_relationship || '',
                emergency_contact_phone: existing.emergency_contact_phone || '',
                is_fleet_partner: existing.is_fleet_partner || false,
                fleet_partner_id: existing.fleet_partner_id || '',
                fleet_partner_company_name: existing.fleet_partner_company_name || '',
                fleet_partner_operator_licence_number: existing.fleet_partner_operator_licence_number || '',
                fleet_partner_operator_licence_expires_at: existing.fleet_partner_operator_licence_expires_at?.slice(0, 10) || '',
                home_address_line1: existing.home_address_line1 || '',
                home_postcode: existing.home_postcode || '',
                operator_address_line1: existing.operator_address_line1 || '',
                operator_postcode: existing.operator_postcode || '',
                also_email_chauffeur: existing.also_email_chauffeur ?? false,
                service_areas: existing.service_areas || [],
            });
        }
    }, [existing, reset]);

    const isFleetPartner = watch('is_fleet_partner');
    const fleetPartnerIdValue = watch('fleet_partner_id');
    // Chauffeur working under an operator → names only are required.
    const underOperator = !isFleetPartner && Boolean(fleetPartnerIdValue);
    // Operators and their chauffeurs: contact details, home address and call sign optional.
    const relaxed = underOperator || Boolean(isFleetPartner);

    const mutation = useMutation({
        mutationFn: async (data) => {
            // Strip call_sign on edit — it's locked, backend won't accept it
            if (isEdit) {
                delete data.call_sign;
            }
            // Clean up operator fields before send
            if (data.is_fleet_partner) {
                // An operator has no "works under" — enforce that client-side
                data.fleet_partner_id = null;
                data.also_email_chauffeur = false;
            } else {
                // Not an operator — clear the operator-only fields (incl. operator address)
                data.fleet_partner_company_name = null;
                data.fleet_partner_operator_licence_number = null;
                data.fleet_partner_operator_licence_expires_at = null;
                data.operator_address_line1 = null;
                data.operator_postcode = null;
                // Normalise empty string to null
                if (data.fleet_partner_id === '') {
                    data.fleet_partner_id = null;
                }
                // The "also email" box only applies under an operator
                if (!data.fleet_partner_id) data.also_email_chauffeur = false;
                // Chauffeurs under an operator use the operator's areas — don't send their own.
                if (data.fleet_partner_id) delete data.service_areas;
            }
            // Empty licence fields → explicit null so the backend clears them
            if (data.phv_licence_number === '') data.phv_licence_number = null;
            if (data.driving_licence_number === '') data.driving_licence_number = null;
            // Keep empty-string stripping for everything else, preserving booleans and explicit nulls
            const payload = Object.fromEntries(
                Object.entries(data).filter(([k, v]) => {
                    // Preserve these booleans & nulls as-is
                    if (k === 'include_in_tfl_report' || k === 'is_fleet_partner' || k === 'also_email_chauffeur') return true;
                    if (k === 'fleet_partner_id') return true;
                    if (k === 'fleet_partner_company_name' || k === 'fleet_partner_operator_licence_number' || k === 'fleet_partner_operator_licence_expires_at') return true;
                    if (k === 'operator_address_line1' || k === 'operator_postcode') return true;
                    if (k === 'phv_licence_number' || k === 'driving_licence_number') return true;
                    // On edit, send cleared contact fields so the backend can blank them
                    // (only possible for chauffeurs under an operator — validation above).
                    if (isEdit && (k === 'last_name' || k === 'email' || k === 'phone' || k === 'home_address_line1')) return true;
                    return v !== '' && v !== null && v !== undefined;
                })
            );
            if (isEdit) {
                return api.patch(`/drivers/${id}`, payload);
            } else {
                return api.post('/drivers', payload);
            }
        },
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['drivers'] });
            queryClient.invalidateQueries({ queryKey: ['fleet-partners'] });
            if (isEdit) {
                queryClient.invalidateQueries({ queryKey: ['driver', id] });
                toast.success('Chauffeur updated');
                navigate(`/chauffeurs/${id}`);
            } else {
                toast.success('Chauffeur created');
                navigate(presetOperatorId ? `/chauffeurs/${presetOperatorId}` : `/chauffeurs/${result.id}`);
            }
        },
        onError: (err) => {
            if (err.fieldErrors) {
                for (const [field, messages] of Object.entries(err.fieldErrors)) {
                    setError(field, { message: Array.isArray(messages) ? messages[0] : messages });
                }
                toast.error('Please fix the errors below');
            } else {
                toast.error(err.message || 'Failed to save');
            }
        },
    });

    if (!isAdmin) {
        return <ErrorPage message="You don't have permission to edit chauffeurs." />;
    }

    if (isEdit && isLoadingExisting) return <LoadingPage />;
    if (isEdit && isError) return <ErrorPage message={error?.message || 'Could not load chauffeur'} />;

    const title = isEdit
        ? `Edit ${existing?.first_name || 'chauffeur'} ${existing?.last_name || ''}`.trim()
        : 'New chauffeur';

    const callSignAlreadySet = isEdit && Boolean(existing?.call_sign);

    return (
        <div className="max-w-2xl">
            <PageHeader
                title={title}
                subtitle={isEdit ? 'Update chauffeur details and reporting settings.' : 'Add a new chauffeur to the system.'}
                backTo={isEdit ? `/chauffeurs/${id}` : (presetOperatorId ? `/chauffeurs/${presetOperatorId}` : '/chauffeurs')}
                backLabel={isEdit ? 'Back to profile' : (presetOperatorId ? 'Back to operator' : 'Back to chauffeurs')}
            />

            <form onSubmit={handleSubmit((data) => mutation.mutate(data))} noValidate>
                <div className="space-y-6">
                    <FormSection
                        title="Operator"
                        subtitle="Set this first — it decides which details are required. Most chauffeurs leave both off (direct Lanz CTS chauffeur)."
                    >
                        <div className="space-y-4">
                            {/* Is an operator */}
                            <div className="flex items-start gap-3 p-3 rounded-md border border-paper-border bg-paper-hover/30">
                                <Building2 className="h-5 w-5 text-ink-subtle mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                    <Checkbox
                                        label="This is an operator"
                                        description="They run their own operator-licensed company with their own chauffeurs and vehicles. Excluded from Lanz CTS reports."
                                        {...register('is_fleet_partner')}
                                        disabled={Boolean(fleetPartnerIdValue)}
                                        error={errors.is_fleet_partner?.message}
                                    />
                                </div>
                            </div>

                            {/* Operator details, revealed when toggle is ON */}
                            {isFleetPartner && (
                                <div className="pl-8 space-y-4 border-l-2 border-paper-border-soft ml-2.5">
                                    <Input
                                        label="Company name"
                                        {...register('fleet_partner_company_name')}
                                        error={errors.fleet_partner_company_name?.message}
                                        hint='e.g. "Hertford Executive Ltd"'
                                    />
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <Input
                                            label="Operator licence number"
                                            {...register('fleet_partner_operator_licence_number')}
                                            error={errors.fleet_partner_operator_licence_number?.message}
                                            hint="TFL or other authority"
                                        />
                                        <Input
                                            label="Operator licence expires"
                                            type="date"
                                            {...register('fleet_partner_operator_licence_expires_at')}
                                            error={errors.fleet_partner_operator_licence_expires_at?.message}
                                        />
                                    </div>

                                    {/* Operator address — optional (Oct 2026) */}
                                    <div>
                                        <label className="block text-xs font-medium text-ink mb-1">
                                            Operator address
                                        </label>
                                        <p className="text-xs text-ink-subtle mb-2">
                                            Optional. Registered office address, if you have it.
                                        </p>
                                        <Controller
                                            name="operator_address_line1"
                                            control={control}
                                            render={({ field }) => (
                                                <AddressInput
                                                    value={field.value || ''}
                                                    onChange={(result) => {
                                                        field.onChange(result.address || '');
                                                        const postcodeField = document.querySelector('input[name="operator_postcode_hidden"]');
                                                        if (postcodeField) postcodeField.value = result.postcode || '';
                                                    }}
                                                    placeholder="Search for operator's registered address..."
                                                    error={errors.operator_address_line1?.message}
                                                />
                                            )}
                                        />
                                        <input type="hidden" {...register('operator_postcode')} name="operator_postcode_hidden" />
                                    </div>
                                </div>
                            )}

                            {/* Works under an operator — only shown if NOT an operator themselves AND at least one operator exists */}
                            {!isFleetPartner && fleetPartners.length > 0 && (
                                <div className="flex items-start gap-3 p-3 rounded-md border border-paper-border bg-paper-hover/30">
                                    <Users className="h-5 w-5 text-ink-subtle mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 space-y-3">
                                        <Select
                                            label="Works under"
                                            {...register('fleet_partner_id')}
                                            error={errors.fleet_partner_id?.message}
                                            hint="Direct = works under Lanz CTS's operator licence. Select an operator if they drive under another operator's licence."
                                        >
                                            <option value="">Direct Lanz CTS chauffeur</option>
                                            {fleetPartners.map(p => (
                                                <option key={p.id} value={p.id}>
                                                    {p.fleet_partner_company_name
                                                        ? `${p.fleet_partner_company_name} (${[p.first_name, p.last_name].filter(Boolean).join(' ')})`
                                                        : [p.first_name, p.last_name].filter(Boolean).join(' ')}
                                                </option>
                                            ))}
                                        </Select>

                                        {underOperator && (
                                            <Checkbox
                                                label="Also send job emails to the chauffeur"
                                                description="The operator always receives job emails and the status link. Tick to send this chauffeur a copy too (needs an email above)."
                                                {...register('also_email_chauffeur')}
                                                error={errors.also_email_chauffeur?.message}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </FormSection>

                    <FormSection
                        title="Personal details"
                        subtitle={isFleetPartner
                            ? 'Operator — only first and last name are required. Add an email so they can receive jobs, status links and statements.'
                            : underOperator
                            ? 'Works under an operator — only the first name is required. Job emails go to the operator.'
                            : undefined}
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input label="First name" required {...register('first_name')} error={errors.first_name?.message} />
                            <Input label="Last name" required={!underOperator || isFleetPartner} {...register('last_name')} error={errors.last_name?.message} />
                            <Input label="Email" type="email" required={!relaxed} {...register('email')} error={errors.email?.message} />
                            <Input label="Phone" type="tel" required={!relaxed} {...register('phone')} error={errors.phone?.message} />
                            <Input label="Date of birth" type="date" {...register('dob')} error={errors.dob?.message} />
                            <Input label="National Insurance" {...register('ni_number')} error={errors.ni_number?.message} hint="e.g. AB123456C" />
                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-ink mb-1">
                                    Home address {!relaxed && <span className="text-red-600">*</span>}
                                </label>
                                <p className="text-xs text-ink-subtle mb-2">
                                    Must match the address on the driving licence and PHV/PHD badge documents.
                                </p>
                                <Controller
                                    name="home_address_line1"
                                    control={control}
                                    render={({ field }) => (
                                        <AddressInput
                                            value={field.value || ''}
                                            onChange={(result) => {
                                                field.onChange(result.address || '');
                                                const postcodeField = document.querySelector('input[name="home_postcode_hidden"]');
                                                if (postcodeField) postcodeField.value = result.postcode || '';
                                            }}
                                            placeholder="Search for chauffeur's home address..."
                                            error={errors.home_address_line1?.message}
                                        />
                                    )}
                                />
                                <input type="hidden" {...register('home_postcode')} name="home_postcode_hidden" />
                            </div>
                        </div>
                    </FormSection>

                    {!underOperator && (
                        <FormSection
                            title="Service areas"
                            subtitle={isFleetPartner
                                ? 'Cities or countries this operator covers. Their chauffeurs use these areas for job offers and the chauffeur list filter.'
                                : 'Cities this chauffeur receives job offers for.'}
                        >
                            <Controller
                                name="service_areas"
                                control={control}
                                render={({ field }) => (
                                    <AreaChipsInput value={field.value || []} onChange={field.onChange} />
                                )}
                            />
                        </FormSection>
                    )}

                    <FormSection title="Identification">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <Input
                                    label={
                                        <span className="inline-flex items-center gap-1.5">
                                            Call sign
                                            {callSignAlreadySet && <Lock className="h-3 w-3 text-ink-subtle" />}
                                        </span>
                                    }
                                    required={!isEdit && !relaxed}
                                    {...register('call_sign')}
                                    error={errors.call_sign?.message}
                                    readOnly={callSignAlreadySet}
                                    className={callSignAlreadySet ? 'opacity-70' : ''}
                                    hint={
                                        callSignAlreadySet
                                            ? 'Call signs are permanent once assigned and cannot be changed.'
                                            : isEdit
                                                ? 'Set a call sign for this chauffeur. Once saved it cannot be changed.'
                                                : relaxed
                                                    ? 'Optional for operators and their chauffeurs. Cannot be changed once set.'
                                                    : 'Short unique identifier (e.g. AD, 101, Charlie-1). Case-sensitive. Cannot be changed later.'
                                    }
                                    maxLength={30}
                                />
                            </div>
                            <Input
                                label="Driving licence number"
                                {...register('driving_licence_number')}
                                error={errors.driving_licence_number?.message}
                                hint="DVLA photocard number"
                            />
                            <Input
                                label="PHD licence number"
                                {...register('phv_licence_number')}
                                error={errors.phv_licence_number?.message}
                                hint="TFL-issued badge number"
                            />
                        </div>
                    </FormSection>

                    <FormSection title="Engagement">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input label="Start date" type="date" required {...register('start_date')} error={errors.start_date?.message} />
                            <Input label="End date" type="date" {...register('end_date')} error={errors.end_date?.message} hint="Leave blank if ongoing" />
                        </div>
                    </FormSection>

                    <FormSection title="Emergency contact" subtitle="Optional. Used if we can't reach the chauffeur (family, next of kin, etc.).">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input label="Contact name" {...register('emergency_contact_name')} error={errors.emergency_contact_name?.message} />
                            <Input
                                label="Relationship"
                                {...register('emergency_contact_relationship')}
                                error={errors.emergency_contact_relationship?.message}
                                hint="e.g. wife, brother, friend"
                            />
                            <Input label="Contact phone" type="tel" {...register('emergency_contact_phone')} error={errors.emergency_contact_phone?.message} className="sm:col-span-2" />
                        </div>
                    </FormSection>

                    <FormSection title="Licensing & reporting">
                        <div className="space-y-4">
                            <Select label="Licensing authority" required {...register('licensing_authority')} error={errors.licensing_authority?.message}>
                                <option value="TFL">TFL</option>
                                <option value="Castle Point">Castle Point</option>
                                <option value="Other">Other</option>
                            </Select>

                            {/* Single checkbox, shown only for direct chauffeurs. */}
                            {!isFleetPartner && !fleetPartnerIdValue && (
                                <Checkbox
                                    label="Include in reports"
                                    description="Untick to exclude this chauffeur from the licensing authority report (e.g. long-term leave, under review)."
                                    {...register('include_in_tfl_report')}
                                    error={errors.include_in_tfl_report?.message}
                                />
                            )}

                            {(isFleetPartner || fleetPartnerIdValue) && (
                                <p className="text-xs text-ink-subtle italic">
                                    {isFleetPartner
                                        ? 'Operators handle their own documents and are always excluded from Lanz CTS reports.'
                                        : 'Chauffeurs working under an operator are always excluded from Lanz CTS reports.'}
                                </p>
                            )}
                        </div>
                    </FormSection>
                </div>

                <div className="mt-8 flex items-center justify-end gap-3 pt-6 border-t border-paper-border-soft">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => navigate(isEdit ? `/chauffeurs/${id}` : (presetOperatorId ? `/chauffeurs/${presetOperatorId}` : '/chauffeurs'))}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" loading={isSubmitting || mutation.isPending} disabled={isEdit && !isDirty}>
                        <Save className="h-4 w-4" />
                        {isEdit ? 'Save changes' : 'Create chauffeur'}
                    </Button>
                </div>
            </form>
        </div>
    );
}

// Chip input for service areas — type a city + Enter, or tap a suggestion.
// Suggestions come from areas already used on other chauffeurs/operators.
function AreaChipsInput({ value, onChange }) {
    const api = useApi();
    const [input, setInput] = useState('');
    const { data } = useQuery({
        queryKey: ['service-areas'],
        queryFn: () => api.get('/offers/meta/service-areas'),
    });
    const has = (a) => value.some((x) => x.toLowerCase() === a.toLowerCase());
    const options = (data?.areas || []).filter((a) => !has(a));

    function titleCase(s) {
        return String(s || '').trim().replace(/\s+/g, ' ').toLowerCase()
            .replace(/(^|[\s-])\S/g, (m) => m.toUpperCase());
    }
    function add(raw) {
        const name = titleCase(raw);
        if (name && !has(name)) onChange([...value, name]);
        setInput('');
    }

    return (
        <div>
            <div className="flex flex-wrap gap-2 mb-3">
                {value.length === 0 && (
                    <span className="text-sm text-ink-subtle italic">No area set — will appear for every area.</span>
                )}
                {value.map((a) => (
                    <span key={a} className="inline-flex items-center gap-1 px-2.5 py-1 text-sm rounded-md bg-ink text-paper">
                        <MapPin className="h-3 w-3 opacity-70" />
                        {a}
                        <button
                            type="button"
                            onClick={() => onChange(value.filter((x) => x !== a))}
                            className="opacity-70 hover:opacity-100"
                            aria-label={`Remove ${a}`}
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </span>
                ))}
            </div>
            <div className="flex gap-2">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); add(input); }
                    }}
                    placeholder="Type a city or country and press Enter"
                    maxLength={60}
                    className="flex-1 px-3 py-1.5 text-sm border border-paper-border rounded-md bg-paper text-ink"
                />
                <Button type="button" variant="secondary" onClick={() => add(input)} disabled={!input.trim()}>
                    Add
                </Button>
            </div>
            {options.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                    {options.map((a) => (
                        <button
                            key={a}
                            type="button"
                            onClick={() => add(a)}
                            className="px-2 py-0.5 text-xs rounded border border-paper-border text-ink-subtle hover:text-ink"
                        >
                            + {a}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function FormSection({ title, subtitle, children }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">{title}</h2>
                {subtitle && <p className="mt-1 text-xs text-ink-subtle">{subtitle}</p>}
            </div>
            <div className="card-body">{children}</div>
        </div>
    );
}
